import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { fileUrl } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Megaphone, Loader2, Plus, ArrowRight, Building2, Layers, CalendarClock, ChevronRight, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { POSITIONS } from '../lib/status';

export default function CampaignsList() {
  const [campaigns, setCampaigns] = useState(null);
  const load = () => api.get('/campaigns').then((r) => setCampaigns(r.data));
  useEffect(() => { load(); }, []);
  if (!campaigns) return <Loader2 className="h-6 w-6 animate-spin text-primary" />;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><p className="overline text-primary flex items-center gap-2"><Megaphone className="h-3.5 w-3.5" /> Campaigns</p><h1 className="font-head text-3xl font-bold">Campaigns</h1></div>
        <NewCampaignDialog onCreated={load} />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {campaigns.map((c) => (
          <Link key={c.id} to={`/campaigns/${c.id}`} data-testid={`campaign-tile-${c.id}`} className="tile-press flex flex-col justify-between rounded-xl border-2 border-border bg-card p-4 hover:border-primary">
            <div>
              <div className="flex items-start justify-between gap-2">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/15 text-primary"><Megaphone className="h-5 w-5" /></div>
                {c.status === 'cancelled'
                  ? <span className="rounded-full bg-rose-500/20 px-2 py-0.5 text-[10px] uppercase text-rose-300">Cancelled</span>
                  : <span className="rounded-full bg-emerald-500/20 px-2 py-0.5 text-[10px] uppercase text-emerald-300">Active</span>}
              </div>
              <p className="mt-3 font-head text-xl font-bold">{c.name}</p>
              <p className="text-sm text-muted-foreground">{c.client_name}</p>
            </div>
            <div className="mt-3 space-y-1">
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><Layers className="h-3.5 w-3.5" /> {c.site_ids?.length || 0} sites mapped</p>
              <p className="flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground"><CalendarClock className="h-3.5 w-3.5" /> {c.start_date || '—'} → {c.end_date || '—'}</p>
              <span className="flex items-center pt-1 text-xs font-semibold text-primary">Open campaign <ArrowRight className="ml-1 h-3.5 w-3.5" /></span>
            </div>
          </Link>
        ))}
        {!campaigns.length && <p className="text-sm text-muted-foreground">No campaigns yet — create one above.</p>}
      </div>
    </div>
  );
}

function NewCampaignDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [societies, setSocieties] = useState([]);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({ name: '', client_name: '', client_user_id: '', society_ids: [], site_ids: [], start_date: '', end_date: '', artwork_file_id: '', site_type: 'mixed', lift_side: 'all' });
  const [saving, setSaving] = useState(false);
  const [layouts, setLayouts] = useState([]);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (!open) return;
    api.get('/societies').then((r) => setSocieties(r.data)).catch(() => {});
    api.get('/users?role=client').then((r) => setClients(r.data)).catch(() => {});
  }, [open]);

  const typeOk = (pt) => {
    const st = form.site_type;
    if (!st || st === 'mixed') return true;
    return st === 'poster' ? (pt === 'poster_left' || pt === 'poster_right') : (pt === 'door_vinyl' || pt === 'door_side');
  };
  const sideOk = (side) => !form.lift_side || form.lift_side === 'all' || side === form.lift_side;

  useEffect(() => {
    if (!open || !form.society_ids.length) { setLayouts([]); return; }
    Promise.all(form.society_ids.map((soc) => api.get(`/societies/${soc}/layout`).then((r) => r.data).catch(() => null))).then((ls) => {
      setLayouts(ls.filter(Boolean));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form.society_ids]);

  const toggleExp = (k) => setExpanded((e) => ({ ...e, [k]: !e[k] }));
  const toggleSite = (id) => setForm((f) => ({ ...f, site_ids: f.site_ids.includes(id) ? f.site_ids.filter((x) => x !== id) : [...f.site_ids, id] }));
  const filteredSites = (l) => (sideOk(l.side) ? (l.sites || []).filter((s) => typeOk(s.position_type)) : []);
  // Positions of a lift/tower/society (filtered by type) for select-all logic
  const groupSiteIds = (sitesArr) => sitesArr.map((s) => s.id);
  const toggleGroup = (ids) => setForm((f) => {
    const allSel = ids.length && ids.every((id) => f.site_ids.includes(id));
    return { ...f, site_ids: allSel ? f.site_ids.filter((x) => !ids.includes(x)) : [...new Set([...f.site_ids, ...ids])] };
  });

  const create = async () => {
    if (!form.name || !form.client_user_id) return toast.error('Campaign name & client are required');
    if (!form.start_date || !form.end_date) return toast.error('Start and end dates are required');
    if (!form.site_ids.length) return toast.error('Select at least one position');
    setSaving(true);
    try {
      await api.post('/campaigns', form);
      toast.success('Campaign created');
      setOpen(false); setForm({ name: '', client_name: '', client_user_id: '', society_ids: [], site_ids: [], start_date: '', end_date: '', artwork_file_id: '', site_type: 'mixed', lift_side: 'all' }); onCreated();
    } catch { toast.error('Failed to create'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="new-campaign-btn"><Plus className="mr-1 h-4 w-4" /> New Campaign</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-head text-2xl">New Campaign</DialogTitle><DialogDescription>Create a campaign, then open it to map its eligible site positions.</DialogDescription></DialogHeader>
        <div className="space-y-2">
          <div><Label>Campaign name</Label><Input data-testid="new-campaign-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><Label>Client</Label>
            <Select value={form.client_user_id} onValueChange={(v) => { const cl = clients.find((c) => c.id === v); setForm({ ...form, client_user_id: v, client_name: cl ? cl.name : '' }); }}>
              <SelectTrigger data-testid="new-campaign-client"><SelectValue placeholder="Select client" /></SelectTrigger>
              <SelectContent>
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.email})</SelectItem>)}
                {!clients.length && <div className="px-3 py-2 text-xs text-muted-foreground">No clients yet — create one on the Clients page</div>}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Site type</Label>
            <Select value={form.site_type} onValueChange={(v) => setForm({ ...form, site_type: v })}>
              <SelectTrigger data-testid="campaign-site-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="poster">Poster</SelectItem>
                <SelectItem value="door">Door</SelectItem>
                <SelectItem value="mixed">All / Mixed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Lift side <span className="text-muted-foreground">(e.g. left posters of left-side lifts only)</span></Label>
            <Select value={form.lift_side} onValueChange={(v) => setForm({ ...form, lift_side: v })}>
              <SelectTrigger data-testid="campaign-lift-side"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All lifts</SelectItem>
                <SelectItem value="left">Left-side lifts only</SelectItem>
                <SelectItem value="right">Right-side lifts only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Societies</Label>
            <div className="max-h-32 space-y-1 overflow-y-auto rounded-lg border border-border p-2">
              {societies.map((s) => (
                <label key={s.id} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={form.society_ids.includes(s.id)} data-testid={`campaign-society-${s.id}`} onCheckedChange={() => setForm((f) => ({ ...f, society_ids: f.society_ids.includes(s.id) ? f.society_ids.filter((x) => x !== s.id) : [...f.society_ids, s.id] }))} />
                  {s.name}
                </label>
              ))}
              {!societies.length && <p className="text-xs text-muted-foreground">No societies yet.</p>}
            </div>
          </div>
          {form.society_ids.length > 0 && (() => {
            const allSites = layouts.flatMap((d) => (d.towers || []).flatMap((t) => t.lifts.flatMap((l) => filteredSites(l))));
            const allSiteIds = allSites.map((s) => s.id);
            const allSel = allSiteIds.length > 0 && allSiteIds.every((id) => form.site_ids.includes(id));
            const typeGroups = ['poster_left', 'poster_right', 'door_vinyl', 'door_side']
              .map((pt) => ({ pt, ids: allSites.filter((s) => s.position_type === pt).map((s) => s.id) }))
              .filter((g) => g.ids.length);
            return (
            <div>
              <div className="flex items-center justify-between">
                <Label>Positions ({form.site_ids.length} selected)</Label>
                {allSiteIds.length > 0 && (
                  <label className="flex cursor-pointer items-center gap-1.5 text-xs font-medium text-primary" data-testid="tree-select-all-label">
                    <Checkbox checked={allSel} data-testid="tree-select-all-global" onCheckedChange={() => toggleGroup(allSiteIds)} />
                    Select all ({allSiteIds.length})
                  </label>
                )}
              </div>
              {typeGroups.length > 1 && (
                <div className="mb-1.5 flex flex-wrap gap-1.5">
                  {typeGroups.map((g) => {
                    const sel = g.ids.every((id) => form.site_ids.includes(id));
                    return (
                      <button type="button" key={g.pt} data-testid={`tree-select-type-${g.pt}`} onClick={() => toggleGroup(g.ids)}
                        className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${sel ? 'border-primary bg-primary/15 text-primary' : 'border-border text-muted-foreground hover:border-primary/50'}`}>
                        {sel ? '✓ ' : '+ '}All {POSITIONS[g.pt]} ({g.ids.length})
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="max-h-56 space-y-1 overflow-y-auto rounded-lg border border-border p-2">
                {layouts.map((d) => {
                  const socName = d.society?.name || '';
                  const socKey = `soc-${d.society?.id}`;
                  const socSiteIds = groupSiteIds((d.towers || []).flatMap((t) => t.lifts.flatMap((l) => filteredSites(l))));
                  const socAll = socSiteIds.length && socSiteIds.every((id) => form.site_ids.includes(id));
                  if (!socSiteIds.length) return null;
                  return (
                    <div key={socKey}>
                      <div className="flex items-center gap-1.5 rounded px-1 py-1 hover:bg-muted/40">
                        <button type="button" onClick={() => toggleExp(socKey)} data-testid={`tree-soc-toggle-${d.society?.id}`} className="text-muted-foreground">{expanded[socKey] === false ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</button>
                        <Checkbox checked={socAll} data-testid={`tree-soc-check-${d.society?.id}`} onCheckedChange={() => toggleGroup(socSiteIds)} />
                        <Building2 className="h-3.5 w-3.5 text-primary" />
                        <span className="text-sm font-semibold">{socName}</span>
                        <span className="ml-auto text-[10px] text-muted-foreground">{socSiteIds.filter((id) => form.site_ids.includes(id)).length}/{socSiteIds.length}</span>
                      </div>
                      {expanded[socKey] !== false && (d.towers || []).map((t) => {
                        const towSiteIds = groupSiteIds(t.lifts.flatMap((l) => filteredSites(l)));
                        if (!towSiteIds.length) return null;
                        const towKey = `tow-${t.id}`;
                        const towAll = towSiteIds.length && towSiteIds.every((id) => form.site_ids.includes(id));
                        return (
                          <div key={towKey} className="ml-4">
                            <div className="flex items-center gap-1.5 rounded px-1 py-1 hover:bg-muted/40">
                              <button type="button" onClick={() => toggleExp(towKey)} data-testid={`tree-tow-toggle-${t.id}`} className="text-muted-foreground">{expanded[towKey] === false ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</button>
                              <Checkbox checked={towAll} data-testid={`tree-tow-check-${t.id}`} onCheckedChange={() => toggleGroup(towSiteIds)} />
                              <span className="text-sm font-medium">{t.name}</span>
                            </div>
                            {expanded[towKey] !== false && t.lifts.map((l) => {
                              const liftSites = filteredSites(l);
                              if (!liftSites.length) return null;
                              const liftIds = groupSiteIds(liftSites);
                              const liftAll = liftIds.every((id) => form.site_ids.includes(id));
                              return (
                                <div key={`lift-${l.id}`} className="ml-5">
                                  <div className="flex items-center gap-1.5 px-1 py-0.5">
                                    <Checkbox checked={liftAll} data-testid={`tree-lift-check-${l.id}`} onCheckedChange={() => toggleGroup(liftIds)} />
                                    <Layers className="h-3 w-3 text-muted-foreground" />
                                    <span className="text-xs font-medium text-muted-foreground">{l.name}</span>
                                  </div>
                                  <div className="ml-6">
                                    {liftSites.map((s) => (
                                      <label key={s.id} className="flex items-center gap-2 py-0.5 text-sm">
                                        <Checkbox checked={form.site_ids.includes(s.id)} data-testid={`new-camp-site-${s.id}`} onCheckedChange={() => toggleSite(s.id)} />
                                        <span className="flex-1">{POSITIONS[s.position_type]}</span>
                                        {s.current_campaign_id && <span className="rounded-full bg-amber-500/20 px-1.5 py-0.5 text-[9px] uppercase text-amber-300">occupied</span>}
                                      </label>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
                {!layouts.length && <p className="text-xs text-muted-foreground">Loading positions…</p>}
                {layouts.length > 0 && !layouts.some((d) => (d.towers || []).some((t) => t.lifts.some((l) => filteredSites(l).length))) && <p className="text-xs text-muted-foreground">No {form.site_type === 'mixed' ? '' : form.site_type + ' '}positions in the selected societies.</p>}
              </div>
            </div>
            );
          })()}
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Start <span className="text-rose-400">*</span></Label><Input type="date" data-testid="campaign-start" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
            <div><Label>End <span className="text-rose-400">*</span></Label><Input type="date" data-testid="campaign-end" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
          </div>
          <div>
            <Label>Poster artwork <span className="text-muted-foreground">(optional — shown to operators to match the board)</span></Label>
            <div className="mt-1 flex items-center gap-3">
              {form.artwork_file_id && <img src={fileUrl(form.artwork_file_id)} alt="artwork" className="h-16 w-16 rounded-md border border-border object-cover" />}
              <label data-testid="campaign-artwork" className="cursor-pointer rounded-md border border-border px-3 py-2 text-sm transition hover:bg-secondary/40">
                {form.artwork_file_id ? 'Change image' : 'Upload image'}
                <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                  const f = e.target.files?.[0]; if (!f) return;
                  const fd = new FormData(); fd.append('file', f);
                  try { const r = await api.post('/upload', fd); setForm((x) => ({ ...x, artwork_file_id: r.data.id })); toast.success('Artwork uploaded'); } catch { toast.error('Upload failed'); }
                }} />
              </label>
            </div>
          </div>
        </div>
        <DialogFooter><Button onClick={create} disabled={saving || !form.name || !form.client_user_id || !form.start_date || !form.end_date || !form.site_ids.length} data-testid="add-campaign-btn">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create Campaign</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
